/**
 * Shared styles for all loop player components
 * These CSS classes provide consistent UI across youtube-loop-player, video-loop-player, and audio-loop-player
 * 
 * Imports sharedStyles from the design system for base button/form/popover styles.
 * Loop player specific classes (.lp-*) extend the base styles with player-specific overrides.
 * 
 * Uses design system variables directly (no --lp-* aliases):
 * - Colors: --player-accent, --player-bg, --player-panel-bg, --player-surface, etc.
 * - Typography: --font-sans, --font-mono
 * - Spacing: --space-*, --radius-*
 * - Sizing: --size-btn-*
 */

import { css, CSSResult } from 'lit';
import { sharedStyles } from '../../styles.js';

/**
 * Loop player specific styles
 * These extend the base styles from sharedStyles with player-specific overrides
 */
const loopPlayerSpecificStyles = css`
  /* ─── Host & Container ─────────────────────────────────────────────────── */
  
  :host {
    display: block;
    font-family: var(--font-sans);
    background: var(--player-bg);
    color: var(--player-text-primary);
    padding: var(--space-4);
    border-radius: var(--radius-lg);
    max-width: 56.25rem; /* 900px */
    margin: 0 auto;
    border: var(--border-width) solid var(--color-border);
  }

  /* Compact mode: reduce padding and border for embedded contexts */
  :host([compact]) {
    padding: var(--space-2);
    border: none;
    background: transparent;
    border-radius: 0;
  }

  .lp-container {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  /* Focus indicator for keyboard navigation */
  :host(:focus) {
    outline: var(--outline-width) solid var(--player-accent);
    outline-offset: var(--outline-offset);
  }

  :host(:focus:not(:focus-visible)) {
    outline: none;
  }

  /* Smooth expand/collapse transition */
  .lp-full-content {
    overflow: hidden;
    transition: max-height 0.3s ease, opacity 0.25s ease;
  }

  @media (prefers-reduced-motion: reduce) {
    .lp-full-content {
      transition: none;
    }
  }

  :host(:not([expanded])) .lp-full-content {
    max-height: 0;
    opacity: 0;
    pointer-events: none;
  }

  /* Move media offscreen when collapsed (YouTube API requires element in DOM) */
  :host(:not([expanded])) .lp-full-content .lp-media-wrapper {
    position: absolute;
    left: -9999px;
    visibility: hidden;
  }

  :host([expanded]) .lp-full-content {
    max-height: 2000px;
    opacity: 1;
  }

  /* Loop controls panel always visible when in loop mode */
  .lp-controls-panel {
    display: none;
  }

  :host([loop-enabled]) .lp-controls-panel {
    display: flex;
    flex-direction: column;
  }

  /* ─── Media Wrapper (16:9) ─────────────────────────────────────────────── */

  .lp-media-wrapper {
    position: relative;
    padding-bottom: 56.25%;
    height: 0;
    overflow: hidden;
    border-radius: var(--radius-md);
    background: #000;
  }

  .lp-media-wrapper iframe,
  .lp-media-wrapper video,
  .lp-media-wrapper audio {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  /* Audio-specific: minimal height, just for native controls */
  .lp-media-wrapper.audio {
    padding-bottom: 0;
    height: auto;
    min-height: 3.375rem; /* 54px */
    background: var(--player-panel-bg);
  }

  .lp-media-wrapper.audio audio {
    position: relative;
    height: 3.375rem; /* 54px */
  }

  /* Hide audio wrapper completely when no countdown */
  .lp-media-wrapper.audio.minimal {
    height: 0;
    min-height: 0;
    overflow: hidden;
  }

  /* ─── Countdown Overlay ────────────────────────────────────────────────── */

  .lp-countdown-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.7);
    z-index: 10;
    border-radius: var(--radius-md);
    cursor: pointer;
  }

  .lp-countdown-number {
    font-size: 7.5rem; /* 120px */
    font-weight: bold;
    color: white;
    text-shadow: 0 0 40px var(--player-accent), 0 4px 8px rgba(0, 0, 0, 0.5);
    animation: lp-countdown-pulse 1s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .lp-countdown-number {
      animation: none;
    }
  }

  @keyframes lp-countdown-pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.1); opacity: 0.8; }
  }

  /* Standalone countdown for audio (no video to overlay) */
  .lp-countdown-standalone {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.85);
    border-radius: var(--radius-md);
    cursor: pointer;
    z-index: 10;
    pointer-events: all;
  }

  .lp-countdown-standalone .lp-countdown-number {
    font-size: 4.5rem; /* 72px */
    font-weight: bold;
    color: white;
    text-shadow: 0 0 20px var(--player-accent), 0 2px 4px rgba(0, 0, 0, 0.5);
  }

  /* ─── Controls Panel ───────────────────────────────────────────────────── */

  /* Styles applied when loop mode is enabled (display is set at top of file) */
  :host([loop-enabled]) .lp-controls-panel {
    background: var(--player-panel-bg);
    border-radius: var(--radius-md);
    padding: var(--space-3);
    gap: var(--space-2-5);
  }

  .lp-control-row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  .lp-control-label {
    font-weight: 500;
    font-size: var(--font-size-sm);
    color: var(--player-text-secondary);
    min-width: 3.75rem; /* 60px */
  }

  /* ─── Playback Controls ────────────────────────────────────────────────── */

  .lp-playback-controls {
    display: flex;
    gap: var(--space-2);
    align-items: center;
  }

  .lp-expand-hint {
    font-family: var(--font-sans);
    font-size: var(--font-size-xs);
    color: var(--player-text-secondary);
    cursor: pointer;
  }

  .lp-expand-hint:hover {
    color: var(--player-accent);
  }

  /* ─── Timeline ─────────────────────────────────────────────────────────── */

  .lp-timeline-container {
    position: relative;
    padding: var(--space-5) 0 var(--space-2);
  }

  .lp-timeline-container.zoomed {
    background: var(--player-surface);
    border-radius: var(--radius-md);
    padding: var(--space-5) var(--space-3) var(--space-2);
    margin: var(--space-2) calc(var(--space-3) * -1) 0;  /* Top margin for spacing from buttons */
    border: var(--border-width-thick) solid var(--player-accent);
  }

  .lp-timeline-loops {
    position: relative;
    height: var(--space-6);
    margin-bottom: var(--space-1);
  }

  .lp-timeline-loop {
    position: absolute;
    height: var(--space-5);
    border-radius: calc(var(--radius-sm) / 2);
    opacity: 0.7;
    cursor: pointer;
    transition: opacity 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--font-size-2xs);
    color: white;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 0 var(--space-0-5, 2px);
    box-sizing: border-box;
  }

  .lp-timeline-loop:hover {
    opacity: 1;
  }

  .lp-timeline-loop.active {
    opacity: 1;
    box-shadow: 0 0 0 2px white;
  }

  .lp-timeline-bar {
    position: relative;
    height: var(--space-2);
    background: var(--player-surface);
    border-radius: var(--radius-sm);
    cursor: pointer;
    /* Expand touch target without changing visual size */
    padding: var(--space-2) 0;
    margin: calc(var(--space-2) * -1) 0;
    background-clip: content-box;
  }

  .lp-timeline-progress {
    position: absolute;
    height: 100%;
    background: var(--player-danger);
    border-radius: calc(var(--radius-sm) / 2);
    pointer-events: none;
  }

  .lp-timeline-position {
    position: absolute;
    top: calc(var(--space-1) * -1);
    width: var(--slider-thumb-size);
    height: var(--slider-thumb-size);
    background: white;
    border-radius: 50%;
    transform: translateX(-50%);
    pointer-events: none;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }

  /* Hover preview cursor */
  .lp-timeline-preview {
    position: absolute;
    top: -32px;
    height: calc(100% + 32px);
    width: var(--border-width-thick);
    background: #fff;
    opacity: 0.9;
    transform: translateX(-50%);
    pointer-events: none;
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.8), 0 0 1px rgba(0, 0, 0, 1);
  }

  .lp-preview-time {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.85);
    color: #fff;
    font-family: var(--font-mono);
    font-size: var(--font-size-2xs);
    padding: var(--space-0-5, 2px) var(--space-1-5);
    border-radius: calc(var(--radius-sm) / 2);
    white-space: nowrap;
  }

  .lp-timeline-times {
    display: flex;
    justify-content: space-between;
    font-size: 0.6875rem; /* 11px - between 2xs and xs */
    color: var(--player-text-secondary);
    margin-top: var(--space-1);
  }

  .lp-timeline-zoom-indicator {
    text-align: center;
    font-size: var(--font-size-2xs);
    color: var(--player-accent);
    margin-top: var(--space-0-5, 2px);
  }

  /* ─── Draggable Loop Handles ───────────────────────────────────────────── */

  .lp-timeline-handles {
    position: relative;
    height: var(--timeline-handle-height);  /* Just handles, times moved to edit panel */
    margin-top: var(--space-5);  /* Clear space below timeline */
    margin-left: var(--space-3);  /* Prevent handle overflow at edges */
    margin-right: var(--space-3);
  }

  .lp-timeline-handle {
    position: absolute;
    width: var(--timeline-handle-width);
    height: var(--timeline-handle-height);
    background: var(--player-accent);
    border-radius: var(--radius-sm);
    cursor: ew-resize;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    user-select: none;
    touch-action: none;
    transition: background 0.15s, transform 0.1s;
  }

  .lp-timeline-handle svg {
    width: var(--space-5);
    height: var(--space-5);
    flex-shrink: 0;
  }

  .lp-timeline-handle:hover {
    background: var(--player-danger);
  }

  .lp-timeline-handle.dragging {
    background: var(--player-danger);
    transform: translateX(-50%) scale(1.1);
    z-index: 10;
  }

  .lp-timeline-handle.active:not(.dragging) {
    background: var(--player-danger);
    box-shadow: 0 0 0 3px rgba(255, 0, 0, 0.3);
  }

  .lp-timeline-handle.start {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .lp-timeline-handle.end {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .lp-timeline-loop-region {
    position: absolute;
    height: var(--slider-track-height);
    top: calc((var(--timeline-handle-height) - var(--slider-track-height)) / 2);
    background: var(--player-accent);
    opacity: 0.5;
    pointer-events: none;
    border-radius: var(--space-0-5, 2px);
  }

  /* ─── Edit Toolbar (reorganized) ────────────────────────────────────────── */

  .lp-edit-toolbar {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-3);
    background: var(--player-item-bg);
    border-radius: var(--radius-md);
    border: var(--border-width-thick) solid var(--player-accent);
  }

  /* Loop actions row - all buttons */
  .lp-loop-actions {
    display: flex;
    align-items: center;
    gap: var(--space-1);
    flex-wrap: wrap;
  }

  /* Separator between button groups */
  .lp-separator {
    width: var(--border-width);
    height: var(--space-5);
    background: var(--player-surface);
    margin: 0 var(--space-1);
  }

  /* Loop info row - name and times */
  .lp-loop-info {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-wrap: wrap;
  }

  /* Loop color dot */
  .lp-loop-color-dot {
    width: var(--space-2-5);
    height: var(--space-2-5);
    border-radius: 50%;
    background: var(--loop-color, var(--player-accent));
    flex-shrink: 0;
  }

  /* Loop selector button with dropdown */
  .lp-loop-selector {
    position: relative;
  }

  .lp-loop-selector-btn {
    background: var(--loop-color, var(--player-accent)) !important;
    color: white !important;
  }

  .lp-loop-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: var(--space-1);
    background: var(--player-panel-bg);
    border: var(--border-width-thick) solid var(--player-accent);
    border-radius: var(--radius-md);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
    z-index: 100;
    min-width: 9.375rem; /* 150px */
    max-height: 12.5rem; /* 200px */
    overflow-y: auto;
  }

  .lp-loop-dropdown-item {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    width: 100%;
    padding: var(--space-2) var(--space-3);
    border: none;
    background: transparent;
    color: var(--player-text-primary);
    font-size: 0.8125rem; /* 13px */
    text-align: left;
    cursor: pointer;
  }

  .lp-loop-dropdown-item:hover {
    background: var(--player-surface);
  }

  .lp-loop-dropdown-item.active {
    background: var(--player-surface);
    font-weight: 500;
  }

  .lp-loop-dropdown-item .lp-loop-color-dot {
    width: var(--space-2);
    height: var(--space-2);
  }

  /* Legacy loop selector dropdown (removed) */
  .lp-loop-select {
    display: none;
  }

  .lp-edit-toolbar .lp-edit-title {
    flex: 1;
    font-weight: 500;
    font-size: var(--font-size-sm);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
    padding: var(--space-0-5, 2px) var(--space-1);
    border-radius: var(--radius-sm);
    user-select: none;
  }

  .lp-edit-toolbar .lp-edit-title:hover {
    background: var(--player-surface);
  }

  .lp-edit-toolbar .lp-edit-title:focus {
    outline: var(--outline-width) solid var(--player-accent);
    outline-offset: var(--border-width);
    background: var(--player-surface);
  }

  .lp-time-value:focus {
    outline: var(--outline-width) solid var(--player-accent);
    outline-offset: var(--border-width);
    background: var(--player-surface);
    border-radius: var(--radius-sm);
  }

  /* Editable name input */
  .lp-edit-name-input {
    flex: 1;
    font-weight: 500;
    font-size: var(--font-size-sm);
    padding: var(--space-0-5, 2px) var(--space-1);
    border: var(--border-width) solid var(--player-accent);
    border-radius: var(--radius-sm);
    background: var(--player-surface);
    color: var(--player-text-primary);
    min-width: 5rem; /* 80px */
  }

  .lp-edit-name-input:focus {
    outline: none;
    border-color: var(--player-accent);
    box-shadow: 0 0 0 var(--border-width-thick) rgba(132, 147, 36, 0.2);
  }

  .lp-loop-times {
    font-family: var(--font-mono);
    font-size: var(--font-size-xs);
    color: var(--player-text-secondary);
    white-space: nowrap;
    padding: var(--space-0-5, 2px) var(--space-2);
    background: var(--player-surface);
    border-radius: var(--radius-sm);
    display: flex;
    align-items: center;
    gap: var(--space-1);
  }

  .lp-time-value {
    cursor: pointer;
    padding: var(--border-width) var(--space-0-5, 2px);
    border-radius: var(--space-0-5, 2px);
    user-select: none;
  }

  .lp-time-value:hover {
    background: var(--player-accent);
    color: white;
  }

  .lp-time-separator {
    color: var(--player-text-secondary);
  }

  /* Editable time input */
  .lp-edit-time-input {
    font-family: var(--font-mono);
    font-size: var(--font-size-xs);
    width: 4.375rem; /* 70px */
    padding: var(--border-width) var(--space-1);
    border: var(--border-width) solid var(--player-accent);
    border-radius: var(--space-0-5, 2px);
    background: var(--player-bg);
    color: var(--player-text-primary);
    text-align: center;
  }

  .lp-edit-time-input:focus {
    outline: none;
    box-shadow: 0 0 0 var(--border-width-thick) rgba(132, 147, 36, 0.2);
  }

  /* Edit/loop actions use .btn.icon.square from ui.css */
  .lp-edit-actions {
    display: flex;
    gap: var(--space-1);
    align-items: center;
  }

  .lp-edit-actions .btn,
  .lp-loop-actions .btn {
    position: relative;
  }

  /* ─── Loop Toggle ──────────────────────────────────────────────────────── */

  .lp-loop-toggle {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }

  .lp-toggle-switch {
    position: relative;
    width: var(--size-btn-xl);
    height: var(--space-6);
    background: var(--player-surface);
    border-radius: var(--radius-lg);
    cursor: pointer;
    transition: background 0.2s;
  }

  .lp-toggle-switch.active {
    background: var(--color-success);
  }

  .lp-toggle-switch::after {
    content: '';
    position: absolute;
    top: var(--space-0-5, 2px);
    left: var(--space-0-5, 2px);
    width: var(--space-5);
    height: var(--space-5);
    background: white;
    border-radius: 50%;
    transition: transform 0.2s;
  }

  .lp-toggle-switch.active::after {
    transform: translateX(var(--space-6));
  }

  /* ─── Loop Indicator ───────────────────────────────────────────────────── */

  .lp-loop-indicator {
    width: var(--space-3);
    height: var(--space-3);
    border-radius: 50%;
    flex-shrink: 0;
  }

  .lp-loop-action-btn {
    width: var(--size-btn-sm);
    height: var(--size-btn-sm);
    padding: 0;
    font-size: var(--font-size-sm);
    background: var(--player-surface);
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .lp-loop-action-btn svg {
    width: var(--font-size-sm);
    height: var(--font-size-sm);
  }

  .lp-loop-action-btn:hover {
    background: var(--player-item-hover-bg);
  }

  /* ─── Shortcuts Help ───────────────────────────────────────────────────── */

  .lp-shortcuts-help {
    background: var(--player-item-bg);
    padding: var(--space-3);
    border-radius: var(--radius-sm);
    font-size: var(--font-size-xs);
    color: var(--player-text-secondary);
  }

  .lp-shortcuts-help strong {
    color: var(--player-accent);
  }

  /* ─── Mobile Responsive ────────────────────────────────────────────────── */

  @media (max-width: 768px) {
    :host {
      padding: var(--space-3);
    }

    .lp-shortcuts-help {
      display: none;
    }

    .lp-timeline-handles {
      height: var(--size-btn-lg);  /* Just handles, times in edit panel */
      margin-left: var(--space-4);  /* Prevent handle overflow at edges */
      margin-right: var(--space-4);
    }

    .lp-timeline-handle {
      width: var(--space-8);
      height: var(--size-btn-lg);
    }

    .lp-timeline-handle svg {
      width: var(--space-6);
      height: var(--space-6);
    }

    .lp-timeline-loop-region {
      top: calc((var(--size-btn-lg) - var(--slider-track-height)) / 2);
    }

    /* Mobile: keep edit toolbar compact */
    .lp-edit-toolbar {
      padding: var(--space-1-5) var(--space-2);
      gap: var(--space-1-5);
    }

    .lp-loop-actions {
      gap: var(--space-0-5, 2px);
    }

    .lp-separator {
      margin: 0 var(--space-0-5, 2px);
    }

    .lp-loop-info {
      gap: var(--space-1-5);
    }
  }

  /* ─── Compact/Unified View ──────────────────────────────────────────────── */

  .lp-compact-view {
    display: flex;
    flex-direction: column;
    gap: var(--space-2-5);
    background: var(--player-panel-bg);
    border-radius: var(--radius-md);
    padding: var(--space-3);
  }

  .lp-compact-header {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .lp-compact-title-group {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-0-5, 2px);
  }

  .lp-compact-title {
    font-weight: 500;
    font-size: 0.9375rem; /* 15px */
    color: var(--player-text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .lp-compact-subtitle {
    font-size: var(--font-size-xs);
    color: var(--player-text-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Unified controls row - same in compact and full mode */
  .lp-controls-row {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-wrap: wrap;
  }

  .lp-playback-btns {
    display: flex;
    align-items: center;
    gap: var(--space-1);
  }

  .lp-playback-btns .btn {
    padding: var(--space-1-5) var(--space-2);
    font-size: var(--font-size-sm);
  }

  /* Controls row time display override */
  .lp-controls-row .time-display {
    font-family: var(--font-mono);
    font-size: 0.8125rem; /* 13px */
    color: var(--player-text-secondary);
    white-space: nowrap;
    margin-left: auto;
  }

  .lp-speed-select {
    font-family: var(--font-mono);
    font-size: var(--font-size-xs);
    padding: var(--space-1) var(--space-1-5);
    border: var(--border-width) solid var(--player-surface);
    border-radius: var(--radius-sm);
    background: var(--player-panel-bg);
    color: var(--player-text-primary);
    cursor: pointer;
    width: unset
  }

  .lp-speed-select:hover {
    border-color: var(--player-accent);
  }

  /* Expand/Collapse Toggle Button */
  .lp-expand-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--size-btn-md);
    height: var(--size-btn-md);
    padding: 0;
    background: var(--player-surface);
    border: none;
    border-radius: var(--space-1-5);
    cursor: pointer;
    transition: background 0.15s;
    flex-shrink: 0;
  }

  .lp-expand-btn:hover {
    background: var(--player-item-hover-bg);
  }

  .lp-expand-btn svg {
    width: var(--space-5);
    height: var(--space-5);
    fill: var(--player-text-primary);
    transition: transform 0.2s ease;
  }

  .lp-expand-btn.expanded svg {
    transform: rotate(180deg);
  }

  /* Mobile: stack controls, time/speed below buttons */
  @media (max-width: 480px) {
    .lp-compact-header {
      flex-wrap: wrap;
    }

    .lp-compact-title {
      flex: 1;
      min-width: 0;
    }

    .lp-controls-row {
      order: 1;
      width: 100%;
      margin-top: var(--space-2);
      flex-wrap: wrap;
      justify-content: space-between;
    }

    .lp-playback-btns {
      width: 100%;
      justify-content: center; /* Center since buttons are now fixed */
      flex-wrap: wrap;
      gap: var(--space-1);
    }

    .time-display {
      order: 10;
      margin-left: 0;
      margin-top: var(--space-2);
    }

    .lp-speed-select {
      order: 11;
      margin-top: var(--space-2);
    }

    .lp-expand-btn {
      order: 0;
    }

    .lp-timeline-bar {
      height: var(--space-3);
      border-radius: var(--space-1-5);
    }

    .lp-timeline-position {
      width: var(--font-size-lg);
      height: var(--font-size-lg);
      top: calc(var(--space-3) * -0.25);
    }

    .lp-speed-select {
      min-height: var(--size-btn-lg);
      padding: var(--space-2) var(--space-3);
      font-size: var(--font-size-base);
    }

    .lp-loop-select {
      min-height: var(--size-btn-lg);
      padding: var(--space-2) 1.75rem var(--space-2) var(--space-3);
      font-size: var(--font-size-base);
    }

    .lp-loop-action-btn {
      width: var(--size-btn-md);
      height: var(--size-btn-md);
    }

    /* Separator becomes a line break on small screens */
    .lp-separator {
      width: 100%;
      height: 0;
      margin: var(--space-1) 0;
      background: transparent;
    }
  }

  /* ─── Time Editing (uses native popover) ───────────────────────────────── */

  .lp-loop-times {
    position: relative;
  }

  .lp-time-value.editing {
    background: var(--player-accent);
    color: white;
  }

  /* Time input styling inside native popover */
  [popover] .popover-input {
    width: 100%;
    box-sizing: border-box;
    font-size: var(--font-size-lg);
    padding: var(--space-2) var(--space-3);
    text-align: center;
    font-family: var(--font-mono);
    margin-bottom: var(--space-2);
  }
`;

/**
 * Complete loop player styles including shared base styles
 * Use this in loop player components: static styles = [loopPlayerStyles, css`...`]
 */
export const loopPlayerStyles: CSSResult[] = [
  ...sharedStyles,
  loopPlayerSpecificStyles
];

